import jsPDF from "jspdf";
import "../../../../assets/font/THSarabunNew-normal";
import "../../../../assets/font/THSarabunNew-bold";
import dayjs from "dayjs";

// ─── helpers ────────────────────────────────────────────────
const str = (v) => (v === null || v === undefined ? "" : String(v));
const dash = (v) => (v === null || v === undefined || v === "" ? "-" : str(v));

const formatMoney = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const num = Number(value);
  if (Number.isNaN(num)) return "-";
  return num.toLocaleString("th-TH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatDate = (dateStr) =>
  dateStr ? dayjs(dateStr).format("DD/MM/YYYY") : "-";

const calcAge = (birthDt) => {
  if (!birthDt) return "-";
  const age = dayjs().diff(dayjs(birthDt), "year");
  return Number.isFinite(age) && age >= 0 ? `${age} ปี` : "-";
};

const personName = (p) =>
  p
    ? `${str(p.SNAM)}${str(p.NAME1)} ${str(p.NAME2)}`
        .replace(/\s+/g, " ")
        .trim()
    : "";

const formatAddress = (addr) => {
  if (!addr) return "";
  return `${str(addr.ADDR1)} ${str(addr.ADDR2)} ต.${dash(addr.TUMB)} อ.${dash(addr.AUMPDES)} จ.${dash(addr.PROVDES)} ${str(addr.ZIP)}${addr.TELP ? ` โทร. ${addr.TELP}` : ""}`
    .replace(/\s+/g, " ")
    .trim();
};

// ─── card data mapping ─────────────────────────────────────
// สัญญาที่มี loanNew จริง: alias ตรงตัว ค่าไม่เปลี่ยนจากเดิม
const buildLoanNewCardData = (loanNew) => ({
  newContno: loanNew?.NEWCONTNO,
  sdate: loanNew?.SDATE,
  grdcod: loanNew?.GRDCOD,
  financedAmount: loanNew?.NCSHPRC,
  totalAmount: loanNew?.LOAN_NEW_TONBALANCE,
  totalPaid: loanNew?.LOAN_NEW_SMPAY,
  installment: loanNew?.TOT_UPAY,
  installmentCount: loanNew?.T_NOPAY,
  firstDueDate: loanNew?.FDATE,
  lastDueDate: loanNew?.LDATE,
  lastPaymentDate: loanNew?.LPAYD,
  lastPaymentAmount: loanNew?.LPAYA,
  expFrm: loanNew?.EXP_FRM,
  expTo: loanNew?.EXP_TO,
  expPrd: loanNew?.EXP_PRD,
  holdNo: loanNew?.HLDNO,
  outstanding: loanNew?.EXP_AMT,
  oldContno: loanNew?.ISSUNO,
});

// สัญญาที่ไม่มี loanNew (พิพากษา): คำนวณยอดชำระ/วันชำระล่าสุดจาก chqtran (+ chqtran_arothr เมื่อ flagLoanNew === 2)
const buildJudgeCardData = (
  judge,
  chqtran = [],
  chqtranArothr = [],
  flagLoanNew,
) => {
  const includeArothr = flagLoanNew === 2;

  const paidRows = [
    ...(chqtran ?? [])
      .filter((r) => r.FLAG !== "C")
      .map((r) => ({ date: r.PAYDT, amount: Number(r.PAYAMT_N || 0) })),
    ...(includeArothr
      ? (chqtranArothr ?? [])
          .filter((r) => r.FLAG !== "C")
          .map((r) => ({ date: r.PAYDT, amount: Number(r.PAYAMT || 0) }))
      : []),
  ];

  const totalPaid = paidRows.reduce((sum, r) => sum + r.amount, 0);

  const lastPaymentDate = paidRows.reduce((latest, r) => {
    if (!r.date) return latest;
    const d = dayjs(r.date);
    return !latest || d.isAfter(latest) ? d : latest;
  }, null);

  const lastPaymentAmount = lastPaymentDate
    ? paidRows
        .filter((r) => r.date && dayjs(r.date).isSame(lastPaymentDate, "day"))
        .reduce((sum, r) => sum + r.amount, 0)
    : null;

  const totalAmount =
    Number(judge?.judgement || 0) +
    Number(judge?.AROTHR || 0) +
    Number(judge?.costOfUselessness || 0);

  // พิพากษา (flagLoanNew === 2): ยอดจัด/ค่างวด = totalJugde, จำนวนงวด = 1
  const isJudgement = flagLoanNew === 2;

  return {
    newContno: judge?.newContno,
    sdate: judge?.SDATE,
    grdcod: null,
    financedAmount: isJudgement ? judge?.totalJugde : judge?.NCSHPRC,
    totalAmount,
    totalPaid,
    installment: isJudgement ? judge?.totalJugde : null,
    installmentCount: isJudgement ? 1 : null,
    firstDueDate: judge?.FDATE,
    lastDueDate: null,
    lastPaymentDate: lastPaymentDate ? lastPaymentDate.toDate() : null,
    lastPaymentAmount,
    expFrm: null,
    expTo: null,
    expPrd: null,
    holdNo: null,
    outstanding: totalAmount - totalPaid,
    oldContno: judge?.CONTNO,
  };
};

const calculatLessPay = (loanNew) => {
  const grade = loanNew?.GRDCOD;
  const totUpay = loanNew?.TOT_UPAY;
  let result;

  if (grade === "A" || grade === "B" || grade === "B") {
    result = totUpay * 1;
  } else if (grade === "D") {
    result = totUpay * 1.25;
  } else if (grade === "X") {
    result = totUpay * 2;
  } else if (grade === "F") {
    result = totUpay * 1.5;
  } else if (grade === "Z") {
    result = totUpay * 10;
  } else {
    result = totUpay;
  }

  return `${formatMoney(result) || "-"} บาท`;
};

const dataBeeypup = (beeypupData) => {
  if (!Array.isArray(beeypupData) || beeypupData.length === 0) return "-";

  const result = beeypupData.reduce((sum, item) => {
    const beeypup = Number(item?.BEEYPUP) || 0;
    const payBeeypup = Number(item?.PAY_BEEYPUP) || 0;
    return sum + (beeypup - payBeeypup);
  }, 0);

  return `${formatMoney(result) || "-"} บาท`;
};

// ─── main export ────────────────────────────────────────────
// ยอดหนี้ทั้งหมด: จาก loanNew (ข้อมูลสัญญา(ใหม่))
// ข้อมูลลูกหนี้/ผู้ค้ำ/รถ: จาก loanData (LOAN เก่า, CUSTOMER, GUARANTORS, MORTGAGE)
export const exportCardDebtorPdf = async ({
  loanNew,
  customer,
  guarantors = [],
  mortgage,
  isJudgeRecord = false,
  chqtran = [],
  chqtranArothr = [],
  flagLoanNew,
  beeypupData = [],
  remarkValueOld,
  judgeData,
  cardData: cardDataOverride, // ส่ง cardData มาเองได้ (เช่น การ์ดสัญญาเก่า)
  title = "การ์ดลูกหนี้",
  showNotes = true, // false = ไม่แสดง บันทึก smart-dev / บันทึกโปรแกรมใหม่
  pdf: existingPdf, // ส่ง jsPDF เดิมมาเพื่อต่อหน้าในไฟล์เดียวกัน
  openWindow = true, // false = ไม่เปิดหน้าต่างพิมพ์ (คืนค่า pdf กลับไป)
}) => {
  if (!loanNew && !customer) return existingPdf;
  console.log("remarkValueOld", remarkValueOld);
  console.log("beeypupData", beeypupData);
  const cardData = cardDataOverride
    ? cardDataOverride
    : isJudgeRecord
      ? buildJudgeCardData(loanNew, chqtran, chqtranArothr, flagLoanNew)
      : buildLoanNewCardData(loanNew);

  const payLess = calculatLessPay(loanNew); //จ่ายขั้นต่ำ
  const beeypupDataUse = dataBeeypup(beeypupData); //เบี้ยปรับ

  const pdf =
    existingPdf ||
    new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  if (existingPdf) pdf.addPage("a4", "portrait");
  const setF = (style = "normal", size = 12) => {
    pdf.setFont("THSarabunNew", style);
    pdf.setFontSize(size);
  };
  const black = () => pdf.setTextColor(0, 0, 0);
  const blue = () => pdf.setTextColor(0, 51, 153);

  const pageW = 210;
  const mL = 14;
  const mR = 14;
  const xR = pageW - mR;
  const contentW = xR - mL;
  const pageH = 297;
  const mB = 12.7; // ขอบล่าง 0.5 นิ้ว
  const maxY = pageH - mB;

  // ── header (นอกกรอบการ์ด) ──
  setF("normal", 11);
  black();
  pdf.text(`วันที่พิมพ์ : ${dayjs().format("DD/MM/YYYY HH:mm:ss")}`, mL, 14);

  setF("bold", 17);
  pdf.text(title, pageW / 2, 15, { align: "center" });
  const titleW = pdf.getTextWidth(title);
  pdf.setDrawColor(0, 0, 0);
  pdf.setLineWidth(0.3);
  pdf.line(pageW / 2 - titleW / 2, 16.5, pageW / 2 + titleW / 2, 16.5);

  setF("bold", 14);
  pdf.text(`เกรด : ${dash(cardData.grdcod)}`, xR, 14, { align: "right" });

  setF("normal", 14);
  pdf.text(`เลขที่สัญญา : ${dash(cardData.newContno)}`, mL, 21);
  pdf.text(`วันทำสัญญา : ${formatDate(cardData.sdate)}`, xR, 21, {
    align: "right",
  });

  // ── กรอบการ์ด (วาดทีหลังเมื่อรู้ความสูงจริง) ──
  const cardTop = 26;
  const padX = mL + 5;
  const innerW = contentW - 10;
  let y = cardTop + 9;
  const lineH = 5;
  const rowGap = 2.5;

  // กรอบการ์ดของแต่ละหน้า (เริ่มที่ cardTop ในหน้าแรก, หน้าถัดไปเริ่มที่ 14)
  let frameTop = cardTop;
  const drawFrame = (top, bottom) => {
    blue();
    pdf.setLineWidth(0.6);
    pdf.rect(mL, top, contentW, bottom - top, "S");
    pdf.setLineWidth(0.25);
    pdf.rect(mL + 1.2, top + 1.2, contentW - 2.4, bottom - top - 2.4, "S");
    black();
  };

  // ถ้าเนื้อหาสูง h ไม่พอในหน้านี้ → ปิดกรอบหน้านี้แล้วขึ้นหน้าใหม่
  const ensureSpace = (h) => {
    if (y + h + 3 <= maxY) return;
    drawFrame(frameTop, maxY);
    pdf.addPage();
    frameTop = 14;
    y = frameTop + 9;
  };

  const colX = (ratios) => {
    let x = padX;
    return ratios.map((r) => {
      const cur = x;
      x += innerW * r;
      return cur;
    });
  };

  const drawFields = (fields, ratios) => {
    const xs = colX(ratios);
    const widths = ratios.map((r) => innerW * r - 4);
    setF("normal", 14);
    black();
    let maxLines = 1;
    const wrapped = fields.map((f, i) => {
      const txt = `${f.label} : ${dash(f.value)}`;
      const lines = pdf.splitTextToSize(txt, widths[i]);
      maxLines = Math.max(maxLines, lines.length);
      return lines;
    });
    ensureSpace(maxLines * lineH);
    wrapped.forEach((lines, i) => {
      lines.forEach((ln, li) => pdf.text(ln, xs[i], y + li * lineH));
    });
    y += maxLines * lineH + rowGap;
  };

  const divider = () => {
    pdf.setDrawColor(190, 190, 190);
    pdf.setLineWidth(0.2);
    pdf.line(padX, y - rowGap + 1, padX + innerW, y - rowGap + 1);
    y += 5;
  };

  const buyerAddr = customer?.ADDRESS?.[0];
  const g1 = guarantors?.[0];
  const g2 = guarantors?.[1];

  // ── ข้อมูลลูกหนี้ (จาก loan) ──
  drawFields(
    [
      { label: "ผู้ซื้อ", value: personName(customer) },
      { label: "อายุ", value: calcAge(customer?.BIRTHDT) },
      { label: "ที่ทำงาน", value: customer?.OFFIC },
    ],
    [0.42, 0.16, 0.42],
  );

  drawFields([{ label: "ที่อยู่", value: formatAddress(buyerAddr) }], [1]);

  drawFields(
    [
      { label: "ผู้ค้ำ 1", value: personName(g1) || "-" },
      { label: "ที่อยู่ผู้ค้ำ 1", value: formatAddress(g1?.ADDRESS?.[0]) },
    ],
    [0.28, 0.72],
  );

  drawFields(
    [
      { label: "ผู้ค้ำ 2", value: personName(g2) || "-" },
      { label: "ที่อยู่ผู้ค้ำ 2", value: formatAddress(g2?.ADDRESS?.[0]) },
    ],
    [0.28, 0.72],
  );

  divider();

  // ── ข้อมูลรถ/หลักประกัน (จาก loan) ──
  drawFields(
    [
      { label: "ยี่ห้อ", value: mortgage?.TYPE },
      { label: "รุ่น", value: mortgage?.MODEL },
      { label: "สี", value: mortgage?.COLOR },
      { label: "ทะเบียน", value: mortgage?.REGNO },
      { label: "ชนิดรถ", value: mortgage?.BAAB },
    ],
    [0.2, 0.2, 0.16, 0.2, 0.24],
  );

  drawFields(
    [
      { label: "เลขตัวเครื่อง", value: mortgage?.ENGNO },
      { label: "เลขตัวถัง", value: mortgage?.STRNO },
      { label: "ปีรถ", value: mortgage?.MANUYR },
    ],
    [0.4, 0.4, 0.2],
  );

  divider();

  // ── ยอดหนี้ (จาก cardData: loanNew หรือ judge fallback) ──
  drawFields(
    [
      {
        label: "ยอดจัดทั้งหมด",
        value: formatMoney(cardData.financedAmount),
      },
      { label: "ชำระแล้ว", value: formatMoney(cardData.totalPaid) },
      { label: "ค่างวด", value: formatMoney(cardData.installment) },
      { label: "จำนวนงวด", value: cardData.installmentCount },
    ],
    [0.28, 0.24, 0.24, 0.24],
  );

  drawFields(
    [
      { label: "งวดแรก", value: formatDate(cardData.firstDueDate) },
      { label: "งวดสุดท้าย", value: formatDate(cardData.lastDueDate) },
      { label: "ชำระล่าสุด", value: formatDate(cardData.lastPaymentDate) },
      {
        label: "ยอดชำระล่าสุด",
        value: formatMoney(cardData.lastPaymentAmount),
      },
    ],
    [0.24, 0.24, 0.24, 0.28],
  );

  divider();

  drawFields(
    [
      { label: "ค้างจากงวดที่", value: cardData.expFrm },
      { label: "ถึงงวดที่", value: cardData.expTo },
      {
        label: "ค้างประมาณ",
        value: cardData.expPrd ? `${cardData.expPrd} งวด` : "-",
      },
      { label: "ค้างจริง", value: cardData.holdNo },
    ],
    [0.28, 0.24, 0.24, 0.24],
  );

  drawFields(
    [
      { label: "ยอดจ่ายขั้นต่ำ", value: payLess },
      { label: "เบี้ยปรับ", value: beeypupDataUse },
    ],
    [0.5, 0.5],
  );

  ensureSpace(lineH);
  setF("bold", 16);
  pdf.setTextColor(178, 34, 34);
  // ยอดค้างชำระติดลบ → แสดง 0
  const outstanding =
    cardData.outstanding == null
      ? cardData.outstanding
      : Math.max(0, Number(cardData.outstanding) || 0);
  pdf.text(`ยอดค้างชำระ : ${formatMoney(outstanding)} บาท`, padX, y);
  y += lineH + rowGap;

  ensureSpace(lineH);
  setF("normal", 14);
  pdf.setTextColor(0, 0, 0);
  pdf.text(`สัญญาเก่า : ${dash(cardData.oldContno)}`, padX, y);
  y += lineH + 4;
  black();

  // ── บันทึก smart-dev (remarkValueOld) ──
  if (showNotes) {
    ensureSpace(lineH * 2 + 1);
    setF("bold", 14);
    pdf.setTextColor(0, 0, 0);
    pdf.text("บันทึก smart-dev :", padX, y);
    y += lineH + 1;
    setF("normal", 14);
    const remarkLines = pdf.splitTextToSize(dash(remarkValueOld), innerW - 4);
    remarkLines.forEach((ln) => {
      ensureSpace(lineH);
      pdf.text(ln, padX + 4, y);
      y += lineH;
    });
    y += 4;
    black();
  }

  // ── บันทึก (note จาก judgeData) ──
  const notes =
    showNotes && Array.isArray(judgeData?.note) ? judgeData.note : [];
  if (notes.length > 0) {
    ensureSpace(lineH * 2 + 1);
    setF("bold", 14);
    pdf.text("บันทึกโปรแกรมใหม่ :", padX, y);
    y += lineH + 1;
    setF("normal", 14);
    notes.forEach((n) => {
      const noteLines = pdf.splitTextToSize(
        `- ${dash(n?.note)} (${dash(n?.USERNAME)} ${formatDate(n?.CreatedAt)})`,
        innerW - 4,
      );
      noteLines.forEach((ln) => {
        ensureSpace(lineH);
        pdf.text(ln, padX + 4, y);
        y += lineH;
      });
      y += 1;
    });
    y += 3;
  }

  // ── กรอบการ์ด ──
  const cardBottom = Math.min(y + 3, maxY);
  drawFrame(frameTop, cardBottom);

  // ── ช่องลงชื่อ: ผู้ติดตาม (ซ้าย) / ผู้อนุมัติ (ขวา) ──
  let sy = cardBottom + 18;
  if (sy + 10 > maxY) {
    pdf.addPage();
    sy = 30;
  }
  const signW = contentW / 2;
  const leftCx = mL + signW / 2;
  const rightCx = mL + signW + signW / 2;
  const dots = ".".repeat(50);
  const enforcementLatest = judgeData?.enforcement?.latest;
  const followerName = enforcementLatest
    ? enforcementLatest.receiveByName
    : judgeData?.lawsult?.NNAME;
  const followerLabel = enforcementLatest
    ? "ผู้ติดตามบังคับคดี"
    : "ผู้ติดตามฟ้อง";
  const followerDate = enforcementLatest
    ? enforcementLatest.receiveDate
    : judgeData?.lawsult?.CreatedAt;

  setF("bold", 14);
  if (followerName)
    pdf.text(str(followerName), leftCx, sy - 2, { align: "center" });

  setF("normal", 14);
  pdf.text(`${followerLabel} ${dots}`, leftCx, sy, { align: "center" });
  pdf.text(`ผู้อนุมัติ ${dots}`, rightCx, sy, { align: "center" });

  sy += 8;
  setF("bold", 14);
  if (followerDate)
    pdf.text(formatDate(followerDate), leftCx + 4, sy - 1, { align: "center" });
  setF("normal", 14);
  pdf.text(`วันที่ ${dots}`, leftCx, sy, { align: "center" });
  pdf.text(`วันที่ ${dots}`, rightCx, sy, { align: "center" });

  if (!openWindow) return pdf;

  // ── เปิด / สั่งพิมพ์ ──
  const blob = pdf.output("blob");
  const url = URL.createObjectURL(blob);
  const win = window.open(url);
  if (win) win.onload = () => win.print();
  else alert("กรุณาปิดการบล็อกป๊อปอัปเพื่อใช้งานฟังก์ชันนี้");
};

export default exportCardDebtorPdf;
